import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isAuthenticated } from "../../../api/client";
import {
  fetchSessionLockConfig,
  SECURITY_SETTINGS_CHANGED_EVENT,
} from "../../../api/securitySettings";
import {
  clearSessionLockStorage,
  clearSessionLocked,
  getLastActivity,
  isSessionLockPersisted,
  persistSessionLocked,
  setLastActivity,
} from "../../../utils/sessionLockStorage";
import SessionLockOverlay from "./SessionLockOverlay";

const SessionLockContext = createContext({
  locked: false,
  resetActivity: () => {},
});

export function useSessionLock() {
  return useContext(SessionLockContext);
}

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "click", "wheel"];

const DEFAULT_CONFIG = {
  session_lock_enabled: true,
  session_lock_timeout_minutes: 5,
};

function readInitialLocked() {
  return isAuthenticated() && isSessionLockPersisted();
}

function SessionLockProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [locked, setLocked] = useState(readInitialLocked);
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const lastActivityRef = useRef(getLastActivity() || Date.now());
  const configRef = useRef(DEFAULT_CONFIG);
  const lockedRef = useRef(readInitialLocked());

  const applyLocked = useCallback(() => {
    lockedRef.current = true;
    persistSessionLocked();
    setLocked(true);
  }, []);

  const applyUnlocked = useCallback(() => {
    lockedRef.current = false;
    clearSessionLocked();
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
    setLocked(false);
  }, []);

  const loadConfig = useCallback(async () => {
    if (!isAuthenticated()) {
      return DEFAULT_CONFIG;
    }
    try {
      const data = await fetchSessionLockConfig();
      const next = {
        session_lock_enabled: Boolean(data.session_lock_enabled),
        session_lock_timeout_minutes: Number(data.session_lock_timeout_minutes) || 5,
      };
      configRef.current = next;
      setConfig(next);
      if (!next.session_lock_enabled) {
        clearSessionLocked();
        lockedRef.current = false;
        setLocked(false);
      }
      return next;
    } catch {
      configRef.current = DEFAULT_CONFIG;
      setConfig(DEFAULT_CONFIG);
      return DEFAULT_CONFIG;
    }
  }, []);

  const resetActivity = useCallback(() => {
    if (lockedRef.current) {
      return;
    }
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
  }, []);

  const checkIdle = useCallback(() => {
    if (!isAuthenticated() || lockedRef.current) {
      return;
    }
    const { session_lock_enabled, session_lock_timeout_minutes } = configRef.current;
    if (!session_lock_enabled) {
      return;
    }
    const timeoutMs = session_lock_timeout_minutes * 60 * 1000;
    if (Date.now() - lastActivityRef.current >= timeoutMs) {
      applyLocked();
    }
  }, [applyLocked]);

  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  useEffect(() => {
    const syncAuth = async () => {
      const authed = isAuthenticated();
      setAuthenticated(authed);
      if (!authed) {
        clearSessionLockStorage();
        lockedRef.current = false;
        setLocked(false);
        return;
      }

      if (isSessionLockPersisted()) {
        lockedRef.current = true;
        setLocked(true);
      } else {
        const stored = getLastActivity();
        if (stored) {
          lastActivityRef.current = stored;
        }
      }

      const nextConfig = await loadConfig();
      if (!isSessionLockPersisted() && nextConfig.session_lock_enabled) {
        checkIdle();
      }
    };
    syncAuth();
    window.addEventListener("credora-auth-changed", syncAuth);
    return () => window.removeEventListener("credora-auth-changed", syncAuth);
  }, [loadConfig, checkIdle]);

  useEffect(() => {
    if (!authenticated) {
      return undefined;
    }
    const onSettingsChanged = async () => {
      const nextConfig = await loadConfig();
      if (!lockedRef.current) {
        const now = Date.now();
        lastActivityRef.current = now;
        setLastActivity(now);
      }
      if (!isSessionLockPersisted() && nextConfig.session_lock_enabled) {
        checkIdle();
      }
    };
    window.addEventListener(SECURITY_SETTINGS_CHANGED_EVENT, onSettingsChanged);
    return () => window.removeEventListener(SECURITY_SETTINGS_CHANGED_EVENT, onSettingsChanged);
  }, [authenticated, loadConfig, checkIdle]);

  useEffect(() => {
    if (!authenticated || locked) {
      return undefined;
    }

    const onActivity = () => resetActivity();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        checkIdle();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibility);

    const intervalId = window.setInterval(checkIdle, 15000);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(intervalId);
    };
  }, [authenticated, locked, resetActivity, checkIdle]);

  const handleUnlocked = useCallback(() => {
    applyUnlocked();
  }, [applyUnlocked]);

  const value = useMemo(
    () => ({
      locked,
      resetActivity,
      config,
    }),
    [locked, resetActivity, config]
  );

  return (
    <SessionLockContext.Provider value={value}>
      {children}
      {authenticated && locked ? <SessionLockOverlay onUnlocked={handleUnlocked} /> : null}
    </SessionLockContext.Provider>
  );
}

export default SessionLockProvider;
